import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { RefreshCw, Sparkles, Link2, Check } from 'lucide-react';
import { apiGetJson as apiGet, apiPostJson as apiPost } from '../../utils/api';
import { modelServicePost } from '../../utils/modelService';

function RunList({ runs }) {
  if (!runs?.length) return <div className="text-gray-500 dark:text-gray-400 py-4 text-sm">No sync runs yet.</div>;
  return (
    <table className="w-full text-sm mt-3">
      <thead className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-200">
        <tr><th className="p-2">Status</th><th className="p-2">Started</th><th className="p-2">Fetched</th><th className="p-2">Updated</th></tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id} className="border-b border-gray-200 dark:border-gray-700">
            <td className="p-2">
              <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'completed' ? 'bg-green-100 text-green-800' : r.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-800'}`}>{r.status}</span>
            </td>
            <td className="p-2 dark:text-gray-300">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
            <td className="p-2 dark:text-gray-300">{r.rows_fetched ?? '—'}</td>
            <td className="p-2 dark:text-gray-300">{r.rows_updated ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const CONFIDENCE_CLS = {
  high: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  medium: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// Audit properties the deterministic backfill couldn't link to the mirror,
// with AI-suggested matches a human confirms one by one.
function PropertyLinks() {
  const qc = useQueryClient();
  const [suggestions, setSuggestions] = useState({}); // audit_property_id -> {processor_property_id, confidence, reason}
  const report = useQuery({ queryKey: ['processor-links'], queryFn: () => apiGet('/admin/audit_tracker/processor_links') });

  const items = [...(report.data?.unmatched || []), ...(report.data?.ambiguous || [])]
    .filter((r) => (r.candidates || []).length > 0);

  const suggest = useMutation({
    mutationFn: async () => {
      const res = await modelServicePost('/audit/ai/match-properties', {
        items: items.map((r) => ({ id: r.id, name: r.name, candidates: r.candidates })),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: (data) => {
      const byId = {};
      (data.matches || []).forEach((m) => { if (m.processor_property_id) byId[m.id] = m; });
      setSuggestions(byId);
      if (Object.keys(byId).length === 0) toast('No confident matches suggested', { icon: 'ℹ️' });
    },
    onError: () => toast.error('Could not get AI suggestions'),
  });

  const apply = useMutation({
    mutationFn: ({ auditPropertyId, processorPropertyId }) =>
      apiPost('/admin/audit_tracker/processor_links', { audit_property_id: auditPropertyId, processor_property_id: processorPropertyId }),
    onSuccess: () => { toast.success('Linked'); qc.invalidateQueries({ queryKey: ['processor-links'] }); },
    onError: () => toast.error('Could not apply the link'),
  });

  if (report.isLoading) return <div className="text-gray-500 dark:text-gray-400 py-4 text-sm">Checking property links…</div>;
  const matchedNote = report.data ? `${report.data.matched} would auto-link by exact name (run the backfill rake task on deploy).` : '';

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {items.length === 0
            ? 'Every audit property is linked to the mirror (or has no plausible candidate). '
            : `${items.length} audit propert${items.length === 1 ? 'y' : 'ies'} couldn't be linked automatically. `}
          {matchedNote}
        </p>
        {items.length > 0 && (
          <button onClick={() => suggest.mutate()} disabled={suggest.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white rounded hover:from-blue-700 hover:to-violet-700 text-sm disabled:opacity-50 shrink-0">
            <Sparkles size={15} /> {suggest.isPending ? 'Matching…' : 'Suggest matches (AI)'}
          </button>
        )}
      </div>

      {items.length > 0 && (
        <table className="w-full text-sm mt-3">
          <thead className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-200">
            <tr><th className="p-2">Audit property</th><th className="p-2">Suggested mirror match</th><th className="p-2">Why</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const s = suggestions[r.id];
              const cand = s && (r.candidates || []).find((c) => c.id === s.processor_property_id);
              return (
                <tr key={r.id} className="border-b border-gray-200 dark:border-gray-700 align-top">
                  <td className="p-2 dark:text-gray-200 font-medium">{r.name}</td>
                  <td className="p-2 dark:text-gray-300">
                    {cand ? (
                      <span>
                        {cand.name} <span className="text-gray-400">({cand.state || '—'} · {cand.company_name || '—'})</span>{' '}
                        <span className={`px-1.5 py-0.5 rounded text-xs ${CONFIDENCE_CLS[s.confidence] || CONFIDENCE_CLS.low}`}>{s.confidence}</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">{Object.keys(suggestions).length ? 'no confident match' : `${(r.candidates || []).length} candidates`}</span>
                    )}
                  </td>
                  <td className="p-2 text-gray-500 dark:text-gray-400 text-xs">{s?.reason || ''}</td>
                  <td className="p-2">
                    {cand && (
                      <button onClick={() => apply.mutate({ auditPropertyId: r.id, processorPropertyId: cand.id })}
                        disabled={apply.isPending}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs disabled:opacity-50">
                        <Check size={13} /> Link
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ProcessorSyncTab() {
  const qc = useQueryClient();
  const [fullReconcile, setFullReconcile] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const mirrorRuns = useQuery({ queryKey: ['mirror-syncs'], queryFn: () => apiGet('/admin/processor_mirror/syncs') });
  const auditRuns = useQuery({ queryKey: ['audit-syncs'], queryFn: () => apiGet('/admin/audit_tracker/processor_syncs') });

  const syncMirror = useMutation({
    mutationFn: () => apiPost('/admin/processor_mirror/syncs', { force_full: fullReconcile }),
    onSuccess: () => { toast.success('Mirror sync queued'); qc.invalidateQueries({ queryKey: ['mirror-syncs'] }); },
    onError: () => toast.error('Could not queue mirror sync'),
  });
  const syncAudit = useMutation({
    mutationFn: () => apiPost('/admin/audit_tracker/processor_syncs', { audit_year: year }),
    onSuccess: () => { toast.success('Audit file-status sync queued'); qc.invalidateQueries({ queryKey: ['audit-syncs'] }); },
    onError: () => toast.error('Could not queue audit sync'),
  });

  return (
    <div className="space-y-8 p-2">
      <section>
        <h3 className="font-dm-serif text-lg dark:text-white mb-2">Mirror replica</h3>
        <div className="flex items-center gap-3">
          <button onClick={() => syncMirror.mutate()} disabled={syncMirror.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50">
            <RefreshCw size={16} /> Sync mirror now
          </button>
          <label className="flex items-center gap-1 text-sm dark:text-gray-200">
            <input type="checkbox" checked={fullReconcile} onChange={(e) => setFullReconcile(e.target.checked)} /> Full reconcile
          </label>
        </div>
        <RunList runs={mirrorRuns.data?.runs} />
      </section>

      <section>
        <h3 className="font-dm-serif text-lg dark:text-white mb-2">Audit file statuses</h3>
        <div className="flex items-center gap-3">
          <input type="number" value={year}
            onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) setYear(n); }}
            className="w-24 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white" />
          <button onClick={() => syncAudit.mutate()} disabled={syncAudit.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm disabled:opacity-50">
            <RefreshCw size={16} /> Pull file statuses
          </button>
        </div>
        <RunList runs={auditRuns.data?.runs} />
      </section>

      <section>
        <h3 className="font-dm-serif text-lg dark:text-white mb-2 flex items-center gap-2"><Link2 size={16} /> Property links</h3>
        <PropertyLinks />
      </section>
    </div>
  );
}
